/**
 * Ficha do cargo — /configuracoes/acessos/cargos/:id.
 *
 * Mesmo layout em 3 zonas (P1) da ficha de usuario, adaptado:
 *   - Header: nome + descricao + badges (nivel, MFA exigido, conflitos SoD)
 *   - Context: KPIs (proprias, efetivas, usuarios no cargo, conflitos SoD)
 *   - Blocks: hierarquia visual (parent), permissoes (matriz), usuarios
 *
 * Bloco RBAC — Cargos autonomos: a hierarquia (parent_id) eh APENAS
 * organizacional/visual. Cada cargo eh autonomo nas permissoes — nao ha
 * mais heranca transitiva de ancestrais. Para customizar individualmente
 * um usuario, use overrides em /configuracoes/acessos/usuarios/{id}.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiEdit, FiShield, FiTrash2 } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import EntityHeader from "../../../components/EntityHeader";
import HierarchyBreadcrumb from "../../../components/HierarchyBreadcrumb";
import KpiStrip from "../../../components/KpiStrip";
import PermissionMatrix from "../../../components/PermissionMatrix";
import { criarNomePermissao } from "../../../components/PermissionMatrix/arvore.js";
import SlideOverPanel from "../../../components/SlideOverPanel";
import SodWarningBanner from "../../../components/SodWarningBanner";
import { useAuth } from "../../../context/AuthContext";

import {
  deletarCargo,
  fetchCargoEfetivas,
  fetchCargoPermissoes,
  fetchCargoUsuarios,
  fetchCargos,
  fetchCatalogoPermissoes,
  patchCargo,
  putCargoPermissoes,
} from "../api";
import {
  Blocks,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ContextRail,
  EmptyMsg,
  ErrorMsg,
  Field,
  InfoMsg,
  InlineForm,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
  Table,
  Textarea,
  ThreeZoneLayout,
} from "../styles.jsx";

function CargoDetalhe() {
  const { cargoId } = useParams();
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  // Cada botão confere a permissão do endpoint que ele chama.
  const podeEditarDados = hasPermission("cargo:edit");
  const podeExcluir = hasPermission("cargo:delete");
  const podeEditarPermissoes = hasPermission("cargo:manage_permissions");
  const podeVerUsuarios = hasPermission("usuario:view");

  const [cargos, setCargos] = useState([]);
  const [efetivas, setEfetivas] = useState(null);
  const [, setPermsAtuais] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [catalogo, setCatalogo] = useState(null);
  const nomePermissao = useMemo(
    () => criarNomePermissao(catalogo?.modulos || []),
    [catalogo]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [selecionados, setSelecionados] = useState(new Set());
  const [salvando, setSalvando] = useState(false);
  const [sodPendente, setSodPendente] = useState(null);
  const [msgSucesso, setMsgSucesso] = useState(null);

  // Sprint 7: edicao dos dados do cargo (nome/descricao/parent/exige_mfa)
  // e exclusao com bloqueios cliente-side.
  const [edicaoOpen, setEdicaoOpen] = useState(false);
  const [edicaoDados, setEdicaoDados] = useState({
    nome: "",
    descricao: "",
    parent_id: "",
    exige_mfa: false,
  });
  const [edicaoSalvando, setEdicaoSalvando] = useState(false);

  // Resiliencia: essencial = dados do cargo + permissoes + catalogo.
  // Opcional = lista de cargos (hierarquia) e usuarios do cargo —
  // se falharem, ficha renderiza com warnings.
  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarnings([]);
    try {
      const [e, p, c] = await Promise.all([
        fetchCargoEfetivas(request, cargoId),
        fetchCargoPermissoes(request, cargoId),
        fetchCatalogoPermissoes(request, true),
      ]);
      setEfetivas(e);
      setPermsAtuais(p?.codigos || []);
      setCatalogo(c);
      setSelecionados(new Set(p?.codigos || []));

      // Opcional: hierarquia (lista de cargos) e usuarios no cargo.
      try {
        const todosCargos = await fetchCargos(request, false);
        setCargos(todosCargos?.resultados || []);
      } catch {
        setCargos([]);
        setWarnings((w) => [
          ...w,
          { key: "hierarquia", message: "Lista de cargos indisponível — hierarquia visível limitada." },
        ]);
      }
      try {
        // Lista de pessoas do cargo e dado de usuario: so com `usuario:view`.
        const u = podeVerUsuarios ? await fetchCargoUsuarios(request, cargoId) : { resultados: [] };
        setUsuarios(u?.resultados || []);
      } catch {
        setUsuarios([]);
        setWarnings((w) => [
          ...w,
          { key: "usuarios", message: "Lista de usuários neste cargo indisponível." },
        ]);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar cargo.");
    } finally {
      setLoading(false);
    }
  }, [request, cargoId, podeVerUsuarios]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cargo = efetivas?.cargo;
  const ancestrais = useMemo(() => {
    // Constroi a cadeia ascendente a partir de `cargos` (lista plana).
    if (!cargo) return [];
    const porId = new Map(cargos.map((c) => [c.id, c]));
    const cadeia = [];
    let atual = porId.get(cargo.id) || cargo;
    let limite = 12;
    while (atual && limite > 0) {
      cadeia.unshift({ id: atual.id, nome: atual.nome });
      atual = atual.parent_id ? porId.get(atual.parent_id) : null;
      limite -= 1;
    }
    if (cadeia.length) cadeia[cadeia.length - 1].current = true;
    return cadeia;
  }, [cargo, cargos]);

  const herdadasSet = useMemo(() => {
    if (!efetivas?.herdadas_de) return new Set();
    const todas = new Set();
    Object.values(efetivas.herdadas_de).forEach((grupo) => {
      (grupo.codigos || []).forEach((c) => todas.add(c));
    });
    return todas;
  }, [efetivas]);

  const proprias = efetivas?.proprias || [];

  const handleToggle = (codigo, novoEstado) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novoEstado) novo.add(codigo);
      else novo.delete(codigo);
      return novo;
    });
  };

  const salvar = async (opts = {}) => {
    setSalvando(true);
    setError(null);
    setSodPendente(null);
    setMsgSucesso(null);
    try {
      const payload = await putCargoPermissoes(
        request,
        cargoId,
        Array.from(selecionados),
        opts
      );
      setMsgSucesso(`Permissões do cargo atualizadas: ${payload?.total || 0}.`);
      setEditorOpen(false);
      await carregar();
    } catch (err) {
      const conflitos = err?.payload?.conflitos || err?.body?.conflitos || null;
      const requer = err?.payload?.requer_aceite || err?.body?.requer_aceite || false;
      if (conflitos) {
        setSodPendente({ conflitos, requer_aceite: requer });
      } else {
        setError(err?.message || "Erro ao salvar permissões do cargo.");
      }
    } finally {
      setSalvando(false);
    }
  };

  const abrirEdicao = () => {
    setEdicaoDados({
      nome: cargo?.nome || "",
      descricao: cargo?.descricao || "",
      parent_id: cargo?.parent_id || "",
      exige_mfa: Boolean(cargo?.exige_mfa),
    });
    setEdicaoOpen(true);
  };

  const salvarEdicao = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setMsgSucesso(null);
    if (!edicaoDados.nome.trim()) {
      setError("O nome do cargo não pode ficar vazio.");
      return;
    }
    setEdicaoSalvando(true);
    try {
      const payload = {
        nome: edicaoDados.nome.trim(),
        descricao: edicaoDados.descricao,
        parent_id: edicaoDados.parent_id || null,
        exige_mfa: edicaoDados.exige_mfa,
      };
      await patchCargo(request, cargoId, payload);
      setMsgSucesso("Dados do cargo atualizados.");
      setEdicaoOpen(false);
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao salvar dados do cargo.");
    } finally {
      setEdicaoSalvando(false);
    }
  };

  const excluirCargo = async () => {
    if (cargo?.nome === "Master") {
      setError("O cargo Master não pode ser excluído.");
      return;
    }
    const totalUsuarios = cargo?.total_usuarios || 0;
    if (totalUsuarios > 0) {
      setError(
        `Este cargo tem ${totalUsuarios} usuário(s) atrelado(s). Mova-os para outro cargo antes de excluir.`
      );
      return;
    }
    if (
      !window.confirm(
        `Excluir cargo "${cargo?.nome}"? Esta ação não pode ser desfeita.`
      )
    ) {
      return;
    }
    try {
      await deletarCargo(request, cargoId);
      navigate("/configuracoes/acessos/cargos");
    } catch (err) {
      // 409 com extras (usuarios_atrelados ou filhos)
      const extras = err?.payload || err?.body || {};
      if (extras?.usuarios_atrelados) {
        setError(
          `Existem ${extras.usuarios_atrelados} usuário(s) atrelado(s) — mova-os primeiro.`
        );
      } else if (Array.isArray(extras?.filhos)) {
        setError(
          `Este cargo é pai de ${extras.filhos.length} cargo(s): ${extras.filhos
            .map((f) => f.nome)
            .join(", ")}. Reorganize a hierarquia antes.`
        );
      } else {
        setError(err?.message || "Erro ao excluir cargo.");
      }
    }
  };

  if (loading) return <Card><EmptyMsg>Carregando ficha do cargo...</EmptyMsg></Card>;
  if (error && !cargo) return <Card><ErrorMsg>{error}</ErrorMsg></Card>;
  if (!cargo) return <Card><EmptyMsg>Cargo não encontrado.</EmptyMsg></Card>;

  const totalProprias = proprias.length;
  // totalHerdadas removido — cargo autonomo nao tem herdadas (Bloco RBAC).
  const totalEfetivas = (efetivas?.efetivas || []).length;
  const totalConflitos = (efetivas?.conflitos_sod || []).length;
  const totalUsuarios = usuarios.length;

  const badges = [
    { label: `Nivel ${cargo.nivel}`, tone: "neutral" },
    cargo.exige_mfa ? { label: "MFA exigido", tone: "info" } : null,
    totalConflitos > 0
      ? {
          label: `${totalConflitos} conflito(s) SoD`,
          tone: "danger",
          icon: <FiAlertTriangle size={12} />,
        }
      : null,
  ].filter(Boolean);

  const kpis = [
    { key: "proprias", label: "Permissões", value: totalProprias },
    // Bloco RBAC — Cargos autonomos: KPI "Herdadas" removida (cargo nao
    // herda mais de ancestrais). totalEfetivas continua = totalProprias agora.
    { key: "efetivas", label: "Efetivas", value: totalEfetivas },
    {
      key: "usuarios",
      label: "Usuários no cargo",
      value: totalUsuarios,
      tone: "neutral",
    },
    {
      key: "sod",
      label: "Conflitos SoD",
      value: totalConflitos,
      tone: totalConflitos > 0 ? "danger" : "default",
    },
  ];

  const ehMaster = cargo.nome === "Master";

  return (
    <>
      <EntityHeader
        onBack={() => navigate("/configuracoes/acessos/cargos")}
        backLabel="Cargos"
        title={cargo.nome}
        subtitle={cargo.descricao || "Cargo do PharuERP"}
        badges={badges}
        primaryAction={
          podeEditarDados
            ? { label: "Editar dados", icon: <FiEdit size={14} />, onClick: abrirEdicao }
            : undefined
        }
        actions={[
          ...(ehMaster || !podeEditarPermissoes
            ? []
            : [
                {
                  label: "Editar permissões",
                  icon: <FiShield size={14} />,
                  onClick: () => setEditorOpen(true),
                },
              ]),
          ...(podeExcluir
            ? [
                {
                  label: "Excluir cargo",
                  icon: <FiTrash2 size={14} />,
                  onClick: excluirCargo,
                  danger: true,
                },
              ]
            : []),
        ]}
      />

      {ancestrais.length > 1 && (
        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <CardTitle>Hierarquia</CardTitle>
          </CardHeader>
          <HierarchyBreadcrumb
            chain={ancestrais}
            onSelect={(id) => navigate(`/configuracoes/acessos/cargos/${id}`)}
          />
        </Card>
      )}

      {msgSucesso && <InfoMsg style={{ marginBottom: 12 }}>{msgSucesso}</InfoMsg>}
      {error && <ErrorMsg style={{ marginBottom: 12 }}>{error}</ErrorMsg>}
      {warnings.length > 0 && (
        <InfoMsg style={{ marginBottom: 12 }}>
          {warnings.map((w) => (
            <div key={w.key}>⚠ {w.message}</div>
          ))}
        </InfoMsg>
      )}

      <ThreeZoneLayout>
        <ContextRail>
          <KpiStrip items={kpis} orientation="vertical" />
        </ContextRail>

        <Blocks>
          <Card>
            <CardHeader>
              <CardTitle>
                Permissões do cargo
                <small style={{ opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>
                  {totalProprias} diretas
                </small>
              </CardTitle>
              {!ehMaster && podeEditarPermissoes && (
                <PrimaryButton type="button" onClick={() => setEditorOpen(true)}>
                  <FiShield size={14} /> Editar
                </PrimaryButton>
              )}
            </CardHeader>
            {ehMaster ? (
              <InfoMsg>
                O cargo <strong>Master</strong> possui acesso a tudo por design.
                Suas permissões não são editáveis via UI.
              </InfoMsg>
            ) : efetivas?.conflitos_sod?.length > 0 ? (
              <SodWarningBanner conflitos={efetivas.conflitos_sod} nomePermissao={nomePermissao} />
            ) : null}
            <PermissionMatrix
              catalogo={catalogo}
              selecionados={new Set(proprias)}
              herdados={herdadasSet}
              onToggle={() => {}}
              disabled
              vazioMensagem="Cargo sem permissões diretas."
            />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Usuários neste cargo</CardTitle>
            </CardHeader>
            {!usuarios.length ? (
              <CardSubtitle>Nenhum usuário atrelado.</CardSubtitle>
            ) : (
              <Table $rowsClickable>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/configuracoes/acessos/usuarios/${u.id}`)}
                    >
                      <td>{u.nome}</td>
                      <td>{u.email}</td>
                      <td>
                        <Pill $tone={u.ativo ? "success" : "danger"}>
                          {u.ativo ? "Ativo" : "Inativo"}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </Blocks>
      </ThreeZoneLayout>

      <SlideOverPanel
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSodPendente(null);
        }}
        title={`Editar permissões do cargo ${cargo.nome}`}
        subtitle="Marque o que quem está neste cargo pode fazer. Clique no ? de cada item para ver o que ele libera."
        width="min(95vw, 880px)"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => {
                setEditorOpen(false);
                setSodPendente(null);
              }}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => salvar()}
              disabled={salvando}
            >
              <FiCheckCircle size={14} /> Salvar
            </PrimaryButton>
          </>
        }
      >
        {sodPendente?.conflitos && (
          <SodWarningBanner
            nomePermissao={nomePermissao}
            conflitos={sodPendente.conflitos}
            onAcceptAnyway={
              sodPendente.requer_aceite
                ? (justif) => salvar({ aceiteConflito: true, justificativa: justif })
                : undefined
            }
            onDismiss={() => setSodPendente(null)}
          />
        )}
        <PermissionMatrix
          catalogo={catalogo}
          selecionados={selecionados}
          herdados={herdadasSet}
          onToggle={handleToggle}
          disabled={salvando}
          vazioMensagem="Catálogo de permissões vazio."
        />
      </SlideOverPanel>

      {/* Sprint 7: editar dados do cargo (nome, descricao, parent, MFA). */}
      <SlideOverPanel
        open={edicaoOpen}
        onClose={() => setEdicaoOpen(false)}
        title={`Editar dados de ${cargo.nome}`}
        subtitle="Altere nome, descrição, hierarquia e política de MFA do cargo."
        width="min(95vw, 520px)"
        footer={
          <>
            <SecondaryButton type="button" onClick={() => setEdicaoOpen(false)}>
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              form="form-editar-cargo"
              disabled={edicaoSalvando}
            >
              <FiCheckCircle size={14} /> Salvar
            </PrimaryButton>
          </>
        }
      >
        {ehMaster && (
          <InfoMsg>
            O cargo Master não pode ser renomeado (o sistema usa o nome para
            identificar o bypass).
          </InfoMsg>
        )}
        <InlineForm id="form-editar-cargo" onSubmit={salvarEdicao}>
          <Field className="full">
            Nome
            <Input
              required
              disabled={ehMaster}
              value={edicaoDados.nome}
              onChange={(e) =>
                setEdicaoDados((p) => ({ ...p, nome: e.target.value }))
              }
            />
          </Field>
          <Field className="full">
            Descrição
            <Textarea
              value={edicaoDados.descricao}
              onChange={(e) =>
                setEdicaoDados((p) => ({ ...p, descricao: e.target.value }))
              }
            />
          </Field>
          <Field>
            Cargo pai
            <Select
              value={edicaoDados.parent_id || ""}
              onChange={(e) =>
                setEdicaoDados((p) => ({ ...p, parent_id: e.target.value }))
              }
            >
              <option value="">Sem cargo pai</option>
              {cargos
                .filter((c) => c.id !== cargo.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
            </Select>
          </Field>
          <Field>
            MFA obrigatório
            <label
              style={{
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                fontSize: "0.85rem",
                fontWeight: 400,
                textTransform: "none",
                marginTop: 6,
              }}
            >
              <input
                type="checkbox"
                checked={edicaoDados.exige_mfa}
                onChange={(e) =>
                  setEdicaoDados((p) => ({ ...p, exige_mfa: e.target.checked }))
                }
              />
              Exigir MFA para usuários neste cargo
            </label>
          </Field>
        </InlineForm>
      </SlideOverPanel>
    </>
  );
}

export default CargoDetalhe;
